import type {
  ChatMessage,
  DetailedPosition,
  LapTime,
  LiveEvent,
  LiveParticipant,
  LiveSession,
  LiveSessionStats,
  SessionReplay,
  TelemetryData
} from '@my-roadtrip/types'
import { calculateRouteDistance } from '@my-roadtrip/utils/geo'
import { toDateOrNow, toNumber as toNumberNormalize } from '../../common/utils/normalize.js'

type SnapshotData = Record<string, any>

type PositionTimeline = {
  userId: string
  userName: string
  positions: DetailedPosition[]
}

type StatsComputationContext = {
  participants?: LiveParticipant[]
  events?: LiveEvent[]
  chat?: ChatMessage[]
  telemetryByUser?: Map<string, TelemetryData[]>
  timelines?: PositionTimeline[]
}

export function buildSessionReplay(
  session: LiveSession,
  snapshot: SnapshotData
): SessionReplay {
  const participants = normalizeParticipants(snapshot?.participants ?? session.participants)
  const events = normalizeEvents(snapshot?.events)
  const chat = normalizeChat(snapshot?.chat)
  const telemetryByUser = normalizeTelemetry(snapshot?.telemetry, session.id)
  const timelines = buildTimelines(participants, telemetryByUser, events)
  const endTime = normalizeDate(snapshot?.endTime ?? session.endTime ?? new Date())
  const startTime = normalizeDate(session.startTime)

  const stats = computeSessionStats(session, snapshot, {
    participants,
    events,
    chat,
    telemetryByUser,
    timelines
  })

  const telemetryArray = Array.from(telemetryByUser.entries()).map(([userId, data]) => ({
    userId,
    data
  }))

  return {
    sessionId: session.id,
    name: session.name,
    type: session.type,
    startTime,
    endTime,
    duration: session.duration ?? Math.max(0, Math.floor((endTime.getTime() - startTime.getTime()) / 1000)),
    positions: timelines,
    events,
    telemetry: telemetryArray.length ? telemetryArray : undefined,
    stats
  }
}

export function computeSessionStats(
  session: LiveSession,
  snapshot: SnapshotData,
  context: StatsComputationContext = {}
): LiveSessionStats {
  const participants =
    context.participants ?? normalizeParticipants(snapshot?.participants ?? session.participants)
  const events = context.events ?? normalizeEvents(snapshot?.events)
  const chat = context.chat ?? normalizeChat(snapshot?.chat)
  const telemetryByUser = context.telemetryByUser ?? normalizeTelemetry(snapshot?.telemetry, session.id)
  const timelines = context.timelines ?? buildTimelines(participants, telemetryByUser, events)
  const laps = resolveLaps(snapshot?.laps)

  const distanceFromParticipants = participants.reduce((total, participant) => {
    return total + (participant.distanceTraveled ?? 0)
  }, 0)

  const distanceFromSession =
    Number(session.totalDistance ?? (session as any)?.distanceMeters ?? (session as any)?.distance ?? 0) || 0

  const distanceFromTimelines = timelines.reduce((total, timeline) => {
    if (timeline.positions.length < 2) return total
    return total + calculateRouteDistance(timeline.positions.map(({ lat, lng }) => ({ lat, lng })))
  }, 0)

  const totalDistance =
    distanceFromParticipants > 0
      ? distanceFromParticipants
      : distanceFromSession > 0
        ? distanceFromSession
        : distanceFromTimelines

  const speedSamples = collectSpeedSamples(telemetryByUser, events, participants)
  const averageSpeed =
    speedSamples.length > 0
      ? speedSamples.reduce((sum, speed) => sum + speed, 0) / speedSamples.length
      : session.averageSpeed || 0
  const maxSpeed = speedSamples.length > 0 ? Math.max(...speedSamples) : session.maxSpeed || 0

  const messageCount = Math.max(
    chat.length,
    events.filter((event) => event.type === 'chat_message').length
  )

  const activeParticipants = participants.filter((participant) => participant.isOnline !== false)
  const stoppedCount = participants.filter((participant) => participant.isStopped).length
  const emergencyCount = events.filter((event) => event.type === 'emergency_alert').length

  const fastestLap = resolveFastestLap(laps)

  return {
    sessionId: session.id,
    totalParticipants: participants.length,
    activeParticipants: activeParticipants.length,
    totalDistance,
    averageSpeed,
    maxSpeed,
    totalLaps: laps.length || undefined,
    fastestLap: fastestLap || undefined,
    emergencyCount,
    stoppedCount,
    messageCount
  }
}

export function renderReplayExport(replay: SessionReplay, format: 'gpx' | 'csv' | 'json'): string {
  if (format === 'json') {
    return JSON.stringify(replay, null, 2)
  }

  if (format === 'csv') {
    const header = 'userId,userName,lat,lng,speed,timestamp'
    const rows: string[] = []

    for (const timeline of replay.positions) {
      for (const position of timeline.positions) {
        const speed = position.speed ?? ''
        rows.push(
          [
            timeline.userId,
            escapeCsv(timeline.userName),
            position.lat.toFixed(6),
            position.lng.toFixed(6),
            speed === '' ? '' : String(speed),
            position.timestamp ? position.timestamp.toISOString() : ''
          ].join(',')
        )
      }
    }

    return [header, ...rows].join('\n')
  }

  const tracks = replay.positions
    .map((timeline) => {
      const points = timeline.positions
        .map(
          (position) => `      <trkpt lat="${position.lat}" lon="${position.lng}">
        <time>${position.timestamp ? position.timestamp.toISOString() : ''}</time>
      </trkpt>`
        )
        .join('\n')

      return `  <trk>
    <name>${escapeXml(timeline.userName)}</name>
    <trkseg>
${points}
    </trkseg>
  </trk>`
    })
    .join('\n')

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="MyRoadTrip" xmlns="http://www.topografix.com/GPX/1/1">
${tracks}
</gpx>`
}

function normalizeParticipants(raw: any): LiveParticipant[] {
  if (!raw) {
    return []
  }

  const participants = Array.isArray(raw) ? raw : Object.values(raw)

  return participants.map((participant: any) => {
    const lastUpdate = normalizeDate(participant?.lastUpdate)
    const position =
      normalizePosition((participant as LiveParticipant)?.position, lastUpdate) ??
      ({
        lat: 0,
        lng: 0,
        timestamp: lastUpdate
      } as DetailedPosition)

    return {
      ...participant,
      userId: participant?.userId,
      sessionId: participant?.sessionId,
      userName: participant?.userName || 'Participant',
      position,
      lastUpdate,
      status: participant?.status || 'moving',
      isOnline: participant?.isOnline ?? false,
      isStopped: participant?.isStopped ?? false,
      stoppedDuration: participant?.stoppedDuration ?? 0,
      distanceTraveled: participant?.distanceTraveled,
      averageSpeed: participant?.averageSpeed,
      maxSpeed: participant?.maxSpeed
    }
  })
}

function normalizeEvents(raw: any): LiveEvent[] {
  if (!raw) {
    return []
  }

  const events = Object.values(raw) as LiveEvent[]

  const normalized = events.map((event) => ({
    ...event,
    timestamp: normalizeDate(event?.timestamp),
    data: event.data ?? {}
  }))
  return [...normalized].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
}

function normalizeChat(raw: any): ChatMessage[] {
  if (!raw) {
    return []
  }

  const messages = Object.values(raw) as ChatMessage[]
  return [...messages]
    .map((message) => ({
      ...message,
      id: message.id || message.sessionId || '',
      timestamp: normalizeDate((message as any)?.timestamp),
      readBy: message.readBy ?? []
    }))
    .slice()
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
}

function normalizeTelemetry(raw: any, sessionId: string): Map<string, TelemetryData[]> {
  const telemetryByUser = new Map<string, TelemetryData[]>()

  if (!raw || typeof raw !== 'object') {
    return telemetryByUser
  }

  for (const [userId, samples] of Object.entries(raw)) {
    const normalizedSamples: TelemetryData[] = []

    for (const sample of Object.values(samples as Record<string, any>)) {
      const timestamp = normalizeDate(sample?.timestamp)
      const normalizedPosition = normalizePosition(
        (sample as TelemetryData)?.position,
        timestamp
      )

      if (!normalizedPosition) {
        continue
      }

      normalizedSamples.push({
        userId,
        sessionId,
        timestamp,
        speed: toNumber((sample as TelemetryData)?.speed) ?? 0,
        acceleration: {
          x: toNumber((sample as TelemetryData)?.acceleration?.x) ?? 0,
          y: toNumber((sample as TelemetryData)?.acceleration?.y) ?? 0,
          z: toNumber((sample as TelemetryData)?.acceleration?.z) ?? 0
        },
        position: normalizedPosition,
        lap: (sample as TelemetryData)?.lap,
        sector: (sample as TelemetryData)?.sector,
        rpm: (sample as TelemetryData)?.rpm,
        gear: (sample as TelemetryData)?.gear,
        throttle: (sample as TelemetryData)?.throttle,
        brake: (sample as TelemetryData)?.brake,
        engineTemp: (sample as TelemetryData)?.engineTemp
      })
    }

    normalizedSamples.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
    telemetryByUser.set(userId, normalizedSamples)
  }

  return telemetryByUser
}

function buildTimelines(
  participants: LiveParticipant[],
  telemetryByUser: Map<string, TelemetryData[]>,
  events: LiveEvent[]
): PositionTimeline[] {
  const participantNames = new Map<string, string>()
  for (const participant of participants) {
    participantNames.set(participant.userId, participant.userName || 'Participant')
  }

  const timelines = new Map<string, PositionTimeline>()
  const ensureTimeline = (userId: string) => {
    if (!timelines.has(userId)) {
      timelines.set(userId, {
        userId,
        userName: participantNames.get(userId) || 'Participant',
        positions: []
      })
    }
    return timelines.get(userId)!
  }

  for (const [userId, samples] of telemetryByUser.entries()) {
    const timeline = ensureTimeline(userId)
    for (const sample of samples) {
      const position = normalizePosition(sample.position, sample.timestamp)
      if (position) {
        timeline.positions.push(position)
      }
    }
  }

  const positionEvents = events.filter((event) => event.type === 'position_updated')
  for (const event of positionEvents) {
    const userId = event.userId ?? event.data?.userId
    const position = normalizePosition(event.data?.position, event.timestamp)
    if (!userId || !position) {
      continue
    }
    const timeline = ensureTimeline(userId)
    timeline.positions.push(position)
  }

  for (const participant of participants) {
    const timeline = ensureTimeline(participant.userId)
    if (timeline.positions.length === 0 && participant.position) {
      const position = normalizePosition(
        participant.position,
        normalizeDate((participant as any)?.lastUpdate ?? new Date())
      )
      if (position) {
        timeline.positions.push(position)
      }
    }
  }

  return Array.from(timelines.values()).map((timeline) => ({
    ...timeline,
    positions: dedupeAndSortPositions(timeline.positions)
  }))
}

function dedupeAndSortPositions(positions: DetailedPosition[]): DetailedPosition[] {
  const sorted = positions
    .filter((position) => Number.isFinite(position.lat) && Number.isFinite(position.lng))
    .sort((a, b) => {
      const aTime = a.timestamp ? a.timestamp.getTime() : 0
      const bTime = b.timestamp ? b.timestamp.getTime() : 0
      return aTime - bTime
    })

  const result: DetailedPosition[] = []
  let previous: DetailedPosition | null = null

  for (const position of sorted) {
    if (
      previous &&
      previous.lat === position.lat &&
      previous.lng === position.lng &&
      (previous.timestamp?.getTime() === position.timestamp?.getTime())
    ) {
      continue
    }
    result.push(position)
    previous = position
  }

  return result
}

function collectSpeedSamples(
  telemetryByUser: Map<string, TelemetryData[]>,
  events: LiveEvent[],
  participants: LiveParticipant[]
): number[] {
  const samples: number[] = []

  for (const telemetry of telemetryByUser.values()) {
    for (const sample of telemetry) {
      if (typeof sample.speed === 'number' && Number.isFinite(sample.speed)) {
        samples.push(sample.speed)
      }
      if (typeof sample.position?.speed === 'number' && Number.isFinite(sample.position.speed)) {
        samples.push(sample.position.speed)
      }
    }
  }

  for (const event of events) {
    const speed = event.data?.position?.speed
    if (typeof speed === 'number' && Number.isFinite(speed)) {
      samples.push(speed)
    }
  }

  for (const participant of participants) {
    const speed =
      participant.position?.speed ??
      participant.averageSpeed ??
      (typeof participant.maxSpeed === 'number' ? participant.maxSpeed : undefined)
    if (typeof speed === 'number' && Number.isFinite(speed)) {
      samples.push(speed)
    }
  }

  return samples
}

function resolveLaps(raw: any): LapTime[] {
  if (!raw) return []
  const laps = Object.values(raw) as LapTime[]
  return [...laps].map((lap) => ({
    ...lap,
    startTime: lap.startTime ? normalizeDate(lap.startTime) : lap.startTime,
    endTime: lap.endTime ? normalizeDate(lap.endTime) : lap.endTime
  }))
}

function resolveFastestLap(laps: LapTime[]): LapTime | null {
  if (!laps.length) return null
  return laps.reduce<LapTime | null>((fastest, lap) => {
    if (lap.duration == null) return fastest
    if (!fastest || (fastest.duration ?? 0) > lap.duration) {
      return lap
    }
    return fastest
  }, null)
}

function normalizePosition(
  raw: Partial<DetailedPosition> | undefined,
  fallbackTimestamp: Date
): DetailedPosition | null {
  if (!raw) {
    return null
  }

  const lat = Number((raw as any)?.lat)
  const lng = Number((raw as any)?.lng)

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null
  }

  const position: DetailedPosition = {
    lat,
    lng,
    timestamp: raw.timestamp ? normalizeDate(raw.timestamp as any) : fallbackTimestamp
  }

  if (raw.accuracy !== undefined && Number.isFinite(Number(raw.accuracy))) {
    position.accuracy = Number(raw.accuracy)
  }
  if (raw.altitude !== undefined && Number.isFinite(Number(raw.altitude))) {
    position.altitude = Number(raw.altitude)
  }
  if (raw.heading !== undefined && Number.isFinite(Number(raw.heading))) {
    position.heading = Number(raw.heading)
  }
  if (raw.speed !== undefined && Number.isFinite(Number(raw.speed))) {
    position.speed = Number(raw.speed)
  }

  return position
}

const normalizeDate = toDateOrNow

function toNumber(value: any): number | undefined {
  const result = toNumberNormalize(value, NaN)
  return Number.isFinite(result) ? result : undefined
}

function escapeCsv(value: string): string {
  if (value == null) return ''
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replaceAll('"', '""')}"`
  }
  return value
}

function escapeXml(value: string): string {
  if (value == null) return ''
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;')
}
